import React, { CSSProperties, useEffect, useState } from 'react';
import { SalariesSummery } from './componenets/salary-summery/salaries-summery';
import { SalaryDateFilters } from './filters/salary-date-filters';
import { SalaryLecturerList } from './filters/salary-lecturer-list';
import 'primereact/resources/themes/lara-light-purple/theme.css';
import { retrieveEmployeeSalaryPaymentStatusOperation } from './operations/retrieve-employee-salary-payment-status-operation';
import { ProfessorsSalariesStatusesType } from '../../../api/types/accountants/salaries-statuses/professors-salaries-statuses.type';
import { toast } from 'react-toastify';
import { generatePayrollReportOperation } from './operations/generate-payroll-report.operation';
import { SalaryProfessorPendingPayments } from './professor-pending-payments/salary-professor-pending-payments';
import { SalariesToolBar } from './componenets/salaries-tool-bar';
import { PendingPaymentBranch } from '../../../api/types/accountants/salaries-pending-payments/salaries-pending-payments.type';
import globalSalaryStore from '../../../stores/accountant/global-salary-store';
import globalSettingsBranches from '../../../stores/global-settings-branches';
import { observer } from 'mobx-react-lite';
import { UserSummaryType } from '../../../api/types/user/user-summary.type';

export interface SelectedRow {
  course: string;
  quantity: number;
  rate: number;
  price: number;
  total: number;
  branchId: string;
  workingHourId: number;
  date?: Date; // Optional date for travel expenses
}

export interface SelectedWorkingHourToPay {
  branchId: number;
  courseId: number;
  courseString: string;
  rate: number;
  rateId: number;
  quantity: number;
  workingHourId: number;
}

export const SalariesGeneratorPage = observer(() => {
  // Calculate default date range (same logic as in SalaryDateFilters)
  const getDefaultDateRange = (): [Date, Date] => {
    const now = new Date();

    // Start date: first day of current month
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);

    // End date (inclusive): last day of current month
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    return [startDate, endDate];
  };

  const [pendingPaymentByBranch, setPendingPaymentByBranch] = useState<
    PendingPaymentBranch[]
  >([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [dateSearchLoading, setDateSearchLoading] = useState(false);
  const [professorStatuses, setProfessorStatuses] =
    useState<ProfessorsSalariesStatusesType>();
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
    getDefaultDateRange(),
  );
  const [
    includeHistoricalMissingPayments,
    setIncludeHistoricalMissingPayments,
  ] = useState(false);

  const fetchEmployeePaymentStatus = async (
    startDate: Date,
    endDate: Date,
    shouldIncludeRetroactive: boolean,
    branchIds: number[],
  ) => {
    await retrieveEmployeeSalaryPaymentStatusOperation(
      startDate,
      endDate,
      shouldIncludeRetroactive,
      branchIds,
      setUsersLoading,
      setProfessorStatuses,
    );
  };

  useEffect(() => {
    const defaultDates = getDefaultDateRange();
    const firstBranchId = (globalSettingsBranches.branches ?? []).find(
      (b) => Number.isInteger(b.id) && b.id >= 1,
    )?.id;
    const branchIds =
      firstBranchId != null ? [firstBranchId] : ([] as number[]);

    fetchEmployeePaymentStatus(
      defaultDates[0],
      defaultDates[1],
      false,
      branchIds,
    );
  }, []);

  const onUserSelected = async (user: UserSummaryType) => {
    const defaultDates = getDefaultDateRange();
    // Use the selected date range if available, otherwise use default dates
    const startDate = dateRange[0] || defaultDates[0];
    const endDate = dateRange[1] || defaultDates[1];

    await generatePayrollReportOperation(
      user,
      startDate,
      endDate,
      includeHistoricalMissingPayments,
      setPendingPaymentByBranch,
    );
  };

  // Clean the state of the selected professor
  const cleanProfessorData = () => {
    globalSalaryStore.user = null;
    globalSalaryStore.selectedWorkingHourToPay = [];
    globalSalaryStore.clearExcludedTravelExpenses();
    setPendingPaymentByBranch([]);
  };

  const handleDateSearch = async (filters: {
    dateRange: [Date | null, Date | null];
    includeHistoricalMissingPayments: boolean;
    branchIds: number[];
  }) => {
    try {
      setDateSearchLoading(true);

      // Store the search filters
      setDateRange(filters.dateRange);
      setIncludeHistoricalMissingPayments(
        filters.includeHistoricalMissingPayments,
      );

      // Refresh the professor statuses with the new date context
      await fetchEmployeePaymentStatus(
        filters.dateRange[0]!,
        filters.dateRange[1]!,
        filters.includeHistoricalMissingPayments,
        filters.branchIds,
      );

      toast.success(
        `חיפוש הושלם בהצלחה עבור התקופה: ${filters.dateRange[0]?.toLocaleDateString('he-IL')} - ${filters.dateRange[1]?.toLocaleDateString('he-IL')}`,
      );

      console.log('Date search completed with filters:', filters);
    } catch (error) {
      toast.error('חיפוש נכשל, נסה שוב בעוד מספר רגעים');
    } finally {
      setDateSearchLoading(false);
    }
  };

  // Convert global store selections to SelectedRow format for backward compatibility
  const convertToSelectedRows = (): SelectedRow[] => {
    return globalSalaryStore.selectedWorkingHourToPay.map((item) => ({
      course: item.courseString,
      quantity: item.quantity,
      rate: item.rate,
      price: item.rate * item.quantity,
      total: item.rate * item.quantity,
      branchId: item.branchId.toString(),
      workingHourId: item.workingHourId,
    }));
  };

  // Get travel expenses as separate rows
  const getTravelExpenseRows = (): SelectedRow[] => {
    const selectedWorkingHourIds = new Set(
      globalSalaryStore.selectedWorkingHourToPay.map(
        (item) => item.workingHourId,
      ),
    );

    // Group travel expenses by date (one per day, using the highest travel amount)
    const dailyTravelMap = new Map<
      string,
      {
        workingHourId: number;
        branchId: string;
        date: Date;
        amount: number;
      }
    >();

    pendingPaymentByBranch.forEach((branch) => {
      branch.courses.forEach((course) => {
        course.rates.forEach((rate) => {
          rate.workingHours.forEach((workingHour) => {
            // Check if this working hour is selected, has addTravel: true, and is not excluded
            if (
              selectedWorkingHourIds.has(workingHour.id) &&
              workingHour.addTravel &&
              !globalSalaryStore.excludedTravelExpenseIds.has(workingHour.id)
            ) {
              const travelAmount = rate.travelRate ?? 0;
              const date = new Date(workingHour.startTime);
              const dateKey = `${date.getFullYear()}-${
                date.getMonth() + 1
              }-${date.getDate()}`;

              const existing = dailyTravelMap.get(dateKey);
              const branchIdStr = branch.branchId.toString();

              if (!existing) {
                dailyTravelMap.set(dateKey, {
                  workingHourId: workingHour.id,
                  branchId: branchIdStr,
                  date,
                  amount: travelAmount,
                });
              } else {
                let amount = existing.amount;
                let workingHourId = existing.workingHourId;
                let branchIdToUse = existing.branchId;

                if (travelAmount > existing.amount) {
                  amount = travelAmount;
                  workingHourId = workingHour.id;
                  branchIdToUse = branchIdStr;
                }

                dailyTravelMap.set(dateKey, {
                  workingHourId,
                  branchId: branchIdToUse,
                  date: existing.date,
                  amount,
                });
              }
            }
          });
        });
      });
    });

    const travelRows: SelectedRow[] = [];

    dailyTravelMap.forEach((value) => {
      if (value.amount > 0) {
        travelRows.push({
          course: 'נסיעות',
          quantity: 1,
          rate: value.amount,
          price: value.amount,
          total: value.amount,
          branchId: value.branchId,
          workingHourId: value.workingHourId,
          date: value.date,
        });
      }
    });

    return travelRows;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <SalariesToolBar selectedRows={convertToSelectedRows()} />

      <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <SalaryDateFilters
            onSearch={handleDateSearch}
            loading={dateSearchLoading}
          />
          <SalaryLecturerList
            professorStatuses={professorStatuses}
            onUserSelected={onUserSelected}
          />
        </div>

        <SalaryProfessorPendingPayments
          pendingPaymentByBranch={pendingPaymentByBranch}
          selectedRows={convertToSelectedRows()}
          setSelectedRows={() => {
            //
          }} // No longer needed, but keeping for interface compatibility
        />
        {globalSalaryStore.user && (
          <SalariesSummery
            onPaymentSuccess={async () => {
              // Update the professor pending for payment
              handleDateSearch({
                dateRange: [dateRange[0]!, dateRange[1]!],
                includeHistoricalMissingPayments:
                  includeHistoricalMissingPayments,
              });
              cleanProfessorData();
            }}
            selectedRows={convertToSelectedRows()}
            travelExpenseRows={getTravelExpenseRows()}
          />
        )}
      </div>
    </div>
  );
});
